import { formatParisDateTime, summarizeCarrierConditions, type CarrierRule } from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { useStoresList } from '@/features/stores/use-stores-list.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import {
  deleteCarrierRule,
  duplicateCarrierRule,
  fetchCarrierRules,
  reorderCarrierRules,
} from './api.js';

export interface CarrierRulesTableProps {
  /** Boutique dont les modes de livraison alimentent les suggestions du tiroir. */
  suggestionsStore?: string | undefined;
}

/**
 * Liste **globale et ordonnée** des règles transporteur (`02` §9, refonte du
 * 10/09/2026) : l'ordre porte la sémantique — la première règle satisfaite
 * gagne — donc les flèches monter/descendre sont l'action principale, pas un
 * confort. Réordonnancement par boutons plutôt que glisser-déposer : aucune
 * bibliothèque de DnD n'est installée, et `02` §9 permet explicitement les deux.
 *
 * L'ajout et l'édition passent par un **tiroir** (`CarrierRuleSheet`), plus de
 * pop-up.
 */
export function CarrierRulesTable(_props: CarrierRulesTableProps): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: queryKeys.carriers.rules(), queryFn: fetchCarrierRules });
  const stores = useStoresList();

  const deleteConfirm = useConfirmDialog<CarrierRule>();

  /** Nom lisible d'une origine dans la colonne « Conditions ». */
  const originName = useMemo(() => {
    const byCode = new Map((stores.data?.items ?? []).map((store) => [store.code, store.name]));
    return (code: string): string => byCode.get(code) ?? code.toUpperCase();
  }, [stores.data]);

  const deleteMutation = useMutation({
    mutationFn: (rule: CarrierRule) => deleteCarrierRule(rule.id),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Règle supprimée.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.carriers.all });
      deleteConfirm.close();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (rule: CarrierRule) => duplicateCarrierRule(rule.id),
    onSuccess: (copy) => {
      toast.success(`Règle « ${copy.name} » créée juste en dessous.`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.carriers.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ruleIds: string[]) => reorderCarrierRules(ruleIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.carriers.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const items = query.data?.items ?? [];

  function move(index: number, direction: -1 | 1): void {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) {
      return;
    }
    const reordered = [...items];
    const [moved] = reordered.splice(index, 1);
    if (!moved) {
      return;
    }
    reordered.splice(targetIndex, 0, moved);
    reorderMutation.mutate(reordered.map((rule) => rule.id));
  }

  const addButton = (
    <Button
      type="button"
      onClick={() => {
        void navigate({ to: '/transporteurs/nouvelle' });
      }}
    >
      <Plus aria-hidden="true" />
      Ajouter une règle
    </Button>
  );

  return (
    <>
      <PageSection
        title="Règles transporteur"
        description="Évaluées de haut en bas : la première règle dont toutes les conditions sont satisfaites l’emporte. Sans règle correspondante, le transporteur par défaut de l’origine s’applique."
        actions={query.isSuccess ? addButton : null}
        flush
      >
        {query.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : query.isError ? (
          <ErrorState
            message={
              query.error instanceof ApiError
                ? query.error.message
                : 'Impossible de charger les règles transporteur.'
            }
            detail={query.error instanceof ApiError ? query.error.detail : undefined}
            onRetry={() => {
              void query.refetch();
            }}
            className="rounded-none border-0"
          />
        ) : items.length === 0 ? (
          <EmptyState
            title="Aucune règle transporteur."
            description="Toutes les commandes partent avec le transporteur par défaut de leur origine."
            action={
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void navigate({ to: '/transporteurs/nouvelle' });
                }}
              >
                Ajouter une règle
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <span className="inline-block w-6 text-right">#</span>
                </TableHead>
                <TableHead>Libellé</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead>Code Martelet</TableHead>
                <TableHead>Point relais</TableHead>
                <TableHead>Dernière utilisation</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((rule, index) => (
                <TableRow key={rule.id} className="cursor-pointer" onClick={() => void navigate({ to: '/transporteurs/$id', params: { id: rule.id } })}>
                  <TableCell>
                    {/* Rang d'évaluation + flèches : l'ordre est la sémantique de la liste. */}
                    <div className="flex items-center gap-1">
                      <span className="w-6 text-right text-sm font-medium tabular-nums text-muted-foreground">
                        {index + 1}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Monter la règle ${rule.name}`}
                        disabled={index === 0 || reorderMutation.isPending}
                        onClick={(event) => {
                          event.stopPropagation();
                          move(index, -1);
                        }}
                      >
                        <ArrowUp aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Descendre la règle ${rule.name}`}
                        disabled={index === items.length - 1 || reorderMutation.isPending}
                        onClick={(event) => {
                          event.stopPropagation();
                          move(index, 1);
                        }}
                      >
                        <ArrowDown aria-hidden="true" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="min-w-40 font-medium">{rule.name}</TableCell>
                  <TableCell className="min-w-56 text-muted-foreground">
                    {summarizeCarrierConditions(rule.conditions, { originName })}
                  </TableCell>
                  <TableCell>
                    <span className="whitespace-nowrap">{rule.result.code}</span>
                    <span className="block text-xs text-muted-foreground">{rule.result.label}</span>
                  </TableCell>
                  <TableCell>{rule.result.pickup ? 'Oui' : 'Non'}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {rule.lastMatchedAt ? (
                      formatParisDateTime(new Date(rule.lastMatchedAt))
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Modifier la règle ${rule.name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          void navigate({ to: '/transporteurs/$id', params: { id: rule.id } });
                        }}
                      >
                        <Pencil aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Dupliquer la règle ${rule.name}`}
                        disabled={duplicateMutation.isPending}
                        onClick={(event) => {
                          event.stopPropagation();
                          duplicateMutation.mutate(rule);
                        }}
                      >
                        <Copy aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Supprimer la règle ${rule.name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteConfirm.open(rule);
                        }}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSection>

      <ConfirmDialog
        open={deleteConfirm.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteConfirm.close();
          }
        }}
        title="Supprimer la règle transporteur ?"
        description={
          deleteConfirm.target
            ? `Les commandes qui correspondaient à « ${deleteConfirm.target.name} » retomberont sur la règle suivante ou, à défaut, sur le transporteur par défaut de leur origine.`
            : ''
        }
        confirmLabel="Supprimer"
        isConfirming={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteConfirm.target) {
            deleteMutation.mutate(deleteConfirm.target);
          }
        }}
      />
    </>
  );
}
